// Geoapify address autocomplete for the signup address fields.
//
// Called from the MEMBER'S BROWSER, inside Auth0's Universal Login page, so
// the key ships in this bundle and is visible to anyone who opens devtools.
// That is how Geoapify's browser integration is designed to work.
//
// ⚠️ The key is UNRESTRICTED. The Geoapify dashboard offers no allowed-origin
// or referrer setting on this plan (checked 2026-10-03), and the API answers
// with `Access-Control-Allow-Origin: *`, so anyone who lifts the key from the
// bundle can spend its credits from anywhere. Accepted for the lab because
// the worst case is bounded: the free plan has no billing, so abuse can only
// exhaust the 3,000/day quota — and an exhausted quota degrades to plain
// text fields (see the catch in AddressAutocompleteField), not a broken
// signup. Rotate the key if that starts happening. It is still kept out of
// git — see vite.config.ts.
//
// Injected via Vite's `define` for the same reason as
// __MEMBER_PORTAL_V2_CLIENT_ID__ (src/utils/flow.ts): `import.meta` is a
// syntax error under Jest's CJS transform.
//
// Unset key = feature off. The address fields then behave exactly as they did
// before this existed — a plain required text input — so a build without the
// key degrades to "no suggestions", never to "can't sign up".
declare const __GEOAPIFY_API_KEY__: string;

export const GEOAPIFY_API_KEY: string | undefined =
  typeof __GEOAPIFY_API_KEY__ === "string" && __GEOAPIFY_API_KEY__.length > 0
    ? __GEOAPIFY_API_KEY__
    : undefined;

const AUTOCOMPLETE_URL = "https://api.geoapify.com/v1/geocode/autocomplete";

export interface AddressSuggestion {
  id: string;
  // Single-line address. This is what lands in the form field, and so in
  // ulp-residential-address / ulp-postal-address — the same free-text string
  // members typed before, so nothing downstream changes.
  formatted: string;
  line1: string;
  line2: string;
}

interface GeoapifyResult {
  place_id?: string;
  formatted?: string;
  address_line1?: string;
  address_line2?: string;
}

// Each request costs one Geoapify credit (free tier: 3,000/day), so callers
// must debounce and respect MIN_QUERY_LENGTH.
export const MIN_QUERY_LENGTH = 3;

export async function fetchAddressSuggestions(
  text: string,
  apiKey: string,
  signal?: AbortSignal
): Promise<AddressSuggestion[]> {
  const params = new URLSearchParams({
    text,
    // Australian super fund: members' addresses are Australian. Typing an
    // overseas address still works — it just gets no suggestions.
    filter: "countrycode:au",
    format: "json",
    lang: "en",
    limit: "5",
    apiKey,
  });

  const res = await fetch(`${AUTOCOMPLETE_URL}?${params}`, { signal });
  if (!res.ok) throw new Error(`Geoapify autocomplete failed: ${res.status}`);

  const body = (await res.json()) as { results?: GeoapifyResult[] };
  return (body.results ?? [])
    .filter((r): r is GeoapifyResult & { formatted: string } => !!r.formatted)
    .map((r, i) => ({
      id: r.place_id ?? `${i}-${r.formatted}`,
      formatted: r.formatted,
      line1: r.address_line1 ?? r.formatted,
      line2: r.address_line2 ?? "",
    }));
}
